import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Eye, EyeOff, LayoutTemplate, Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type RowAction,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { ContentBlockStatus, ContentSection } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { useStore } from '@/shared/store/store-context';

import { DeleteSectionDialog } from '../components/delete-section-dialog';
import { useSectionColumns } from '../components/section-columns';
import { sectionFilters } from '../components/section-filters';
import { useUpdateSection } from '../hooks/use-section-mutations';
import { useSections } from '../hooks/use-sections';

const ROUTE_ID = '/_authed/_active/s/$storeId/content/sections/' as const;
const ROUTE_PATH = '/s/$storeId/content/sections/' as const;

function NewSectionButton({ storeId, tour }: { storeId: string; tour?: string }) {
  const { t } = useTranslation('content');
  return (
    <Can permission="content.write">
      <Button asChild data-tour={tour}>
        <Link to="/s/$storeId/content/sections/new" params={{ storeId }}>
          <Plus aria-hidden />
          {t('sectionsList.new')}
        </Link>
      </Button>
    </Can>
  );
}

function Sections() {
  const { t } = useTranslation('content');
  const store = useStore();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const message = useErrorMessage();
  const { can } = usePermissions();
  const canWrite = can('content.write');
  const update = useUpdateSection(store.id);
  const [deleting, setDeleting] = useState<ContentSection | null>(null);

  const filters = useMemo(() => sectionFilters(t), [t]);
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const query = useSections(store.id, list.params);

  const mutateStatus = update.mutate;
  const setStatus = useCallback(
    (row: ContentSection, status: ContentBlockStatus) =>
      mutateStatus(
        { id: row.id, body: { status } },
        { onError: (error) => toast.error(message(error)) },
      ),
    [message, mutateStatus],
  );

  const open = useCallback(
    (row: ContentSection) =>
      void navigate({
        to: '/s/$storeId/content/sections/$sectionId',
        params: { storeId: store.id, sectionId: row.id },
      }),
    [navigate, store.id],
  );

  const rowActions = useMemo<Array<RowAction<ContentSection>>>(
    () => [
      { id: 'open', label: t('actions.open'), icon: <Pencil aria-hidden />, onAction: open },
      {
        id: 'draft',
        label: t('actions.setDraft'),
        icon: <EyeOff aria-hidden />,
        separatorBefore: true,
        hidden: (row) => !canWrite || row.status !== 'active',
        onAction: (row) => setStatus(row, 'draft'),
      },
      {
        id: 'activate',
        label: t('actions.activate'),
        icon: <Eye aria-hidden />,
        separatorBefore: true,
        hidden: (row) => !canWrite || row.status === 'active',
        onAction: (row) => setStatus(row, 'active'),
      },
      {
        id: 'delete',
        label: t('actions.delete'),
        icon: <Trash2 aria-hidden />,
        variant: 'destructive',
        separatorBefore: true,
        hidden: () => !canWrite,
        onAction: (row) => setDeleting(row),
      },
    ],
    [canWrite, open, setStatus, t],
  );

  const columns = useSectionColumns(store.id);
  const rows = query.data?.items ?? [];
  const table = useDataTable({
    mode: 'server',
    data: rows,
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'sections',
  });

  const narrowed = Boolean(list.q) || Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && rows.length === 0 && !narrowed;

  return (
    <PageContent>
      <PageHeader
        title={t('sectionsList.title')}
        description={t('sectionsList.description', { store: store.name })}
        actions={<NewSectionButton storeId={store.id} tour="content.sections.new" />}
      />
      {isEmptyList ? (
        <EmptyState
          icon={LayoutTemplate}
          title={t('sectionsList.empty.title')}
          description={t('sectionsList.empty.description')}
          action={<NewSectionButton storeId={store.id} />}
          tour="content.sections.empty"
          tourLink
        />
      ) : (
        <DataTable
          table={table}
          tour={{ table: 'content.sections.table' }}
          search={{ placeholder: t('sectionsList.search'), label: t('sectionsList.search') }}
          loading={query.isPending}
          fetching={query.isFetching || update.isPending}
          error={query.error}
          onRetry={() => void query.refetch()}
          rowActions={rowActions}
          onRowClick={open}
          getRowLabel={(row) => row.name}
          resourceName={{
            singular: t('sectionsList.resource.one'),
            plural: t('sectionsList.resource.other'),
          }}
          emptyState={{ title: t('sectionsList.noMatches') }}
        />
      )}
      <DeleteSectionDialog
        storeId={store.id}
        row={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </PageContent>
  );
}

export function SectionsPage() {
  return (
    <FeatureErrorBoundary name="content.sections">
      <Sections />
    </FeatureErrorBoundary>
  );
}
